import { useState, useEffect } from 'react';
import { Z_PLAYER_HUD } from '../../../features/quickstart/config/zIndexLayers';

type WishlistButtonPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

interface AnimatedWishlistButtonProps {
  position: WishlistButtonPosition;
  stylePosition?: WishlistButtonPosition;
  isMobile: boolean;
  onClick: () => void;
  text?: string;
  animationDuration?: number;
  shouldRotate?: boolean;
}

/**
 * Animated wishlist button with rotating gradient border effect
 * Features a 4-frame gradient animation that cycles around the border
 * Supports all 4 corner positions with appropriate border radius
 */
export const AnimatedWishlistButton = ({
  position,
  stylePosition,
  isMobile,
  onClick,
  text = 'Wishlist\nNow',
  animationDuration = 1000,
  shouldRotate = false,
}: AnimatedWishlistButtonProps) => {
  const [currentFrame, setCurrentFrame] = useState(0);
  
  // Use stylePosition for border radius/padding when provided (for rotation cases)
  // Otherwise use position for both placement and styling
  const positionForStyling = stylePosition || position;

  // Easing function for single focal point
  // Creates slow motion near 0/1 (270°), fast motion in middle (90°)
  const easeInOutCubic = (x: number) => {
    return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
  };

  // Generate 32 frames with single focal point at 270°
  // Slows near 270° (start/end), fast at 90° (middle/opposite)
  // Counter-clockwise rotation
  const gradientFrames = Array.from({ length: 32 }, (_, i) => {
    const t = i / 31; // Normalize 0 to 1 (31 not 32 to avoid duplicate at wrap)
    const easedT = easeInOutCubic(t); // Apply easing
    const angle = 270 - (easedT * 360); // Counter-clockwise: 270 → -90
    const normalizedAngle = angle < 0 ? angle + 360 : angle; // Wrap -90 back to 270
    return `${normalizedAngle.toFixed(2)}deg`;
  });

  // Cycle through gradient frames - each frame shows for animationDuration/32
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFrame((prev) => (prev + 1) % 32);
    }, animationDuration / 32);

    return () => clearInterval(interval);
  }, [animationDuration]);

  // Position classes for each corner
  // When rotated to forced landscape, physical top/bottom become visual left/right,
  // so the margin must shift to physical left/right (which become visual top/bottom)
  const positionClasses: Record<WishlistButtonPosition, string> = shouldRotate ? {
    'top-left': 'left-3 top-0',
    'top-right': 'right-3 top-0',
    'bottom-left': 'left-3 bottom-0',
    'bottom-right': 'right-3 bottom-0',
  } : {
    'top-left': 'left-0 top-3',
    'top-right': 'right-0 top-3',
    'bottom-left': 'left-0 bottom-3',
    'bottom-right': 'right-0 bottom-3',
  };

  // Border radius based on position
  // Rounds the side AWAY from screen edge (left-rounded for right positions, right-rounded for left positions)
  // Border radius - pill shaped on side away from screen, sharp on sides touching screen
  const borderRadius: Record<WishlistButtonPosition, string> = {
    'top-left': '0 50px 50px 0',     // pill on right side, sharp on left & top
    'top-right': '50px 0 0 50px',    // pill on left side, sharp on right & top
    'bottom-left': '0 50px 50px 0',  // pill on right side, sharp on left & bottom
    'bottom-right': '50px 0 0 50px', // pill on left side, sharp on right & bottom
  };

  // Padding for outer container (creates the gradient border effect)
  // Zero padding only on the vertical edge that's flush with screen (left or right)
  const outerPadding: Record<WishlistButtonPosition, string> = {
    'top-left': '7px 7px 7px 0',     // No padding on left edge only, gradient on top/right/bottom
    'top-right': '7px 0 7px 7px',    // No padding on right edge only, gradient on top/bottom/left
    'bottom-left': '7px 7px 7px 0',  // No padding on left edge only, gradient on top/right/bottom
    'bottom-right': '7px 0 7px 7px', // No padding on right edge only, gradient on top/bottom/left
  };

  // Inner padding (text positioning within white container)
  const innerPadding = isMobile ? '15px 8px 10px 8px' : '15px 8px 10px 8px';

  // Process text into lines
  // If text has explicit \n, use those. Otherwise, intelligently split long text.
  let textLines: string[];
  if (text.includes('\n')) {
    // User explicitly specified line breaks
    textLines = text.split('\n').filter(line => line.trim());
  } else {
    // Auto-split by spaces - distribute words across lines
    const words = text.trim().split(/\s+/);
    if (words.length === 1) {
      // Single long word - keep as is (will expand width)
      textLines = [words[0]];
    } else if (words.length === 2) {
      // Two words - one per line
      textLines = words;
    } else if (words.length === 3) {
      // Three words - try to split 2:1 or 1:2
      textLines = [words.slice(0, 2).join(' '), words[2]];
    } else {
      // Four or more words - split in half
      const mid = Math.ceil(words.length / 2);
      textLines = [
        words.slice(0, mid).join(' '),
        words.slice(mid).join(' ')
      ];
    }
  }

  // Calculate dynamic width based on longest line
  const fontSize = isMobile ? 16 : 18;
  const estimatedCharWidth = fontSize * 0.6; // Approximate character width for bold text
  const longestLine = textLines.reduce((max, line) => 
    line.length > max.length ? line : max, ''
  );
  const estimatedTextWidth = longestLine.length * estimatedCharWidth;
  const minWidth = isMobile ? 90 : 110;
  const maxWidth = isMobile ? 180 : 220;
  // Account for: gradient border (7px × 2) + inner padding (8px × 2) + extra spacing
  const totalHorizontalPadding = (7 * 2) + (8 * 2) + 16; 
  const calculatedWidth = Math.max(minWidth, Math.min(maxWidth, estimatedTextWidth + totalHorizontalPadding));

  // Button dimensions
  const width = calculatedWidth;
  const height = isMobile ? 79 : 90;

  // Current gradient for animation
  const currentGradient = `linear-gradient(${gradientFrames[currentFrame]}, rgb(251, 191, 36) 4%, rgb(107, 33, 168) 100%)`;

  return (
    <div 
      className={`fixed ${positionClasses[position]}`}
      style={{
        zIndex: Z_PLAYER_HUD,
        ...(shouldRotate ? {
          width: `${height}px`,
          height: `${width}px`,
        } : undefined),
      }}
    >
      {/* Outer container with animated gradient border */}
      <div
        style={{
          width: `${width}px`,
          height: `${height}px`,
          backgroundImage: currentGradient,
          borderRadius: borderRadius[positionForStyling],
          padding: outerPadding[positionForStyling],
          overflow: 'hidden',
          transition: `background-image ${animationDuration / 32}ms linear`,
          // Counter-rotate the button when parent view is rotated
          transform: shouldRotate ? 'rotate(90deg)' : undefined,
          transformOrigin: 'center center',
          // Position the rotated element correctly
          position: shouldRotate ? 'absolute' : undefined,
          top: shouldRotate ? '50%' : undefined,
          left: shouldRotate ? '50%' : undefined,
          marginTop: shouldRotate ? `${-height / 2}px` : undefined,
          marginLeft: shouldRotate ? `${-width / 2}px` : undefined,
        }}
      >
        {/* Inner white container */}
        <button
          onClick={onClick}
          tabIndex={-1}
          className='flex h-full w-full flex-col items-center justify-center bg-white transition-opacity hover:opacity-90'
          style={{
            borderRadius: borderRadius[positionForStyling],
            padding: innerPadding,
            touchAction: 'auto',
          }}
        >
          {/* Gradient text */}
          <div
            className='flex flex-col items-center justify-center text-center font-black uppercase leading-tight'
            style={{
              fontSize: isMobile ? '16px' : '18px',
              background: 'linear-gradient(90deg, rgb(107, 33, 168) 8%, rgb(168, 85, 247) 111%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {textLines.map((line, index) => (
              <span key={index} style={{ display: 'block', margin: 0 }}>
                {line}
              </span>
            ))}
          </div>
        </button>
      </div>
    </div>
  );
};
